import type { Content } from '~/types/blog'
import { normalizeFieldText, readFieldValue } from '~/composables/useCustomFields'

type PathContext = {
  cid: string
  slug: string
  category: string
  directory: string
  year: string
  month: string
  day: string
}

type SimpleCategory = {
  ID?: number
  slug?: string
  children?: SimpleCategory[]
}

const DEFAULT_POST_STYLE = 'wordpress'
const POST_STYLE_DEFAULT = '/archives/{cid}/'
const POST_STYLE_WORDPRESS = '/archives/{slug}.html'
const POST_STYLE_DATE = '/{year}/{month}/{day}/{slug}.html'
const POST_STYLE_CATEGORY = '/{category}/{slug}.html'

const ensureLeadingSlash = (value: string) => {
  const text = String(value || '').trim()
  if (!text) {
    return '/'
  }
  return text.startsWith('/') ? text : `/${text}`
}

const collapseSlash = (value: string) => value.replace(/\/+/g, '/').replace(/\/{2,}/g, '/').trim()

const trimTrailingSlash = (value: string) => {
  if (value === '/') {
    return '/'
  }
  return value.replace(/\/+$/, '')
}

const pad2 = (value: number) => String(value).padStart(2, '0')

const extractDateParts = (content?: Content) => {
  const source = content?.publishedAt || content?.CreatedAt || ''
  const date = source ? new Date(source) : new Date()
  if (Number.isNaN(date.getTime())) {
    const now = new Date()
    return {
      year: String(now.getFullYear()),
      month: pad2(now.getMonth() + 1),
      day: pad2(now.getDate())
    }
  }
  return {
    year: String(date.getFullYear()),
    month: pad2(date.getMonth() + 1),
    day: pad2(date.getDate())
  }
}

const normalizePattern = (value: string, fallback: string) => {
  const next = trimTrailingSlash(collapseSlash(ensureLeadingSlash(value || fallback)))
  return next || fallback
}

const isValidPattern = (pattern: string, requiredTokens: string[]) => requiredTokens.some(token => pattern.includes(token))

const readPostPattern = (fields: Record<string, unknown> | undefined) => {
  const style = normalizeFieldText(fields, 'postPathStyle', DEFAULT_POST_STYLE)
  const custom = normalizeFieldText(fields, 'postPathCustom', '')
  if (style === 'default') {
    return POST_STYLE_DEFAULT
  }
  if (style === 'date') {
    return POST_STYLE_DATE
  }
  if (style === 'category') {
    return POST_STYLE_CATEGORY
  }
  if (style === 'custom') {
    const candidate = normalizePattern(custom, POST_STYLE_WORDPRESS)
    return isValidPattern(candidate, ['{slug}', '{cid}']) ? candidate : POST_STYLE_WORDPRESS
  }
  return POST_STYLE_WORDPRESS
}

const readPagePattern = (fields: Record<string, unknown> | undefined) => {
  const raw = normalizeFieldText(fields, 'pagePathPattern', '/{slug}.html')
  const candidate = normalizePattern(raw, '/{slug}.html')
  return isValidPattern(candidate, ['{slug}', '{cid}']) ? candidate : '/{slug}.html'
}

const readCategoryPattern = (fields: Record<string, unknown> | undefined) => {
  const raw = normalizeFieldText(fields, 'categoryPathPattern', '/category/{directory}/')
  const candidate = normalizePattern(raw, '/category/{directory}/')
  return isValidPattern(candidate, ['{directory}', '{slug}', '{mid}']) ? candidate : '/category/{directory}/'
}

const normalizeInputPath = (value: string) => {
  const hashIndex = value.indexOf('#')
  const queryIndex = value.indexOf('?')
  const end = [hashIndex, queryIndex].filter(index => index >= 0).sort((a, b) => a - b)[0] ?? value.length
  const clean = value.slice(0, end).trim()
  return trimTrailingSlash(collapseSlash(ensureLeadingSlash(clean))) || '/'
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const tokenToRegex = (token: string) => {
  if (token === 'cid' || token === 'mid') {
    return '(?<cid>\\d+)'
  }
  if (token === 'directory') {
    return '(?<directory>.+?)'
  }
  if (token === 'year') {
    return '(?<year>\\d{4})'
  }
  if (token === 'month' || token === 'day') {
    return `(?<${token}>\\d{1,2})`
  }
  return `(?<${token}>[^/]+)`
}

const buildPatternMatcher = (pattern: string) => {
  const normalized = normalizePattern(pattern, '/')
  const tokenPattern = /\{(cid|mid|slug|category|directory|year|month|day)\}/g
  let source = '^'
  let lastIndex = 0
  let matched: RegExpExecArray | null = null
  while ((matched = tokenPattern.exec(normalized)) !== null) {
    source += escapeRegExp(normalized.slice(lastIndex, matched.index))
    source += tokenToRegex(matched[1])
    lastIndex = matched.index + matched[0].length
  }
  source += escapeRegExp(normalized.slice(lastIndex))
  source += '/?$'
  return new RegExp(source)
}

const parseByPattern = (pattern: string, path: string) => {
  const matcher = buildPatternMatcher(pattern)
  const matched = matcher.exec(normalizeInputPath(path))
  if (!matched?.groups) {
    return null
  }
  const read = (key: string) => decodeURIComponent(String(matched?.groups?.[key] || '').trim())
  const cid = read('cid')
  const slug = read('slug')
  const category = read('category')
  const directory = read('directory')
  const year = read('year')
  const month = read('month')
  const day = read('day')

  // 获取当前链接的模式
  const hasCidToken = pattern.includes('{cid}')
  const hasSlugToken = pattern.includes('{slug}')

  let mode: 'cid' | 'slug' | 'none' = 'none'
  if (hasCidToken && cid) {
    mode = 'cid'
  } else if (hasSlugToken && slug) {
    mode = 'slug'
  }

  return {
    mode,
    cid,
    slug,
    category,
    directory,
    year,
    month,
    day
  }
}

export const getPostPathPattern = (fields: Record<string, unknown> | undefined) => readPostPattern(fields)

export const getPagePathPattern = (fields: Record<string, unknown> | undefined) => readPagePattern(fields)

export const getCategoryPathPattern = (fields: Record<string, unknown> | undefined) => readCategoryPattern(fields)

const buildByPattern = (pattern: string, ctx: PathContext) => {
  const path = pattern
    .replaceAll('{cid}', encodeURIComponent(ctx.cid))
    .replaceAll('{mid}', encodeURIComponent(ctx.cid))
    .replaceAll('{slug}', encodeURIComponent(ctx.slug))
    .replaceAll('{category}', encodeURIComponent(ctx.category))
    .replaceAll('{directory}', ctx.directory.split('/').map(part => encodeURIComponent(part)).join('/'))
    .replaceAll('{year}', encodeURIComponent(ctx.year))
    .replaceAll('{month}', encodeURIComponent(ctx.month))
    .replaceAll('{day}', encodeURIComponent(ctx.day))
  return trimTrailingSlash(collapseSlash(ensureLeadingSlash(path))) || '/'
}

const findDirectory = (nodes: SimpleCategory[], targetSlug: string, parent: string[] = []): string => {
  for (const item of nodes || []) {
    const slug = String(item?.slug || '').trim()
    if (!slug) {
      continue
    }
    const next = [...parent, slug]
    if (slug === targetSlug) {
      return next.join('/')
    }
    const child = findDirectory(item.children || [], targetSlug, next)
    if (child) {
      return child
    }
  }
  return ''
}

export const buildCategoryDirectoryMap = (nodes: SimpleCategory[]) => {
  const map = new Map<number, string>()
  const walk = (items: SimpleCategory[], parent: string[] = []) => {
    for (const item of items || []) {
      const slug = String(item?.slug || '').trim()
      const id = Number(item?.ID || 0)
      if (!slug || !id) {
        continue
      }
      const next = [...parent, slug]
      map.set(id, next.join('/'))
      walk(item.children || [], next)
    }
  }
  walk(nodes || [])
  return map
}

const buildContext = (
  content: Partial<Content> | { ID?: number; slug?: string },
  opts?: { directory?: string; category?: string }
): PathContext => {
  const slug = String(content?.slug || '')
  const cid = String(content?.ID || '')
  const date = extractDateParts(content as Content)
  const directory = String(opts?.directory || '').trim() || String(opts?.category || '').trim()
  const category = String(opts?.category || '').trim() || (directory.split('/').filter(Boolean).pop() || '')
  return {
    cid,
    slug,
    category,
    directory,
    year: date.year,
    month: date.month,
    day: date.day
  }
}

export const buildPostPath = (
  fields: Record<string, unknown> | undefined,
  content: Partial<Content> | { ID?: number; slug?: string },
  opts?: { directory?: string; category?: string }
) => {
  const pattern = readPostPattern(fields)
  const ctx = buildContext(content, opts)
  if (!ctx.slug && !ctx.cid) {
    return '/'
  }
  return buildByPattern(pattern, ctx)
}

export const buildPagePath = (fields: Record<string, unknown> | undefined, page: { ID?: number; slug?: string }) => {
  const pattern = readPagePattern(fields)
  const ctx = buildContext(page)
  if (!ctx.slug && !ctx.cid) {
    return '/'
  }
  return buildByPattern(pattern, ctx)
}

export const buildCategoryPath = (
  fields: Record<string, unknown> | undefined,
  category: { ID?: number; slug?: string },
  opts?: { directory?: string; categoryTree?: SimpleCategory[] }
) => {
  const pattern = readCategoryPattern(fields)
  const slug = String(category?.slug || '').trim()
  const id = Number(category?.ID || 0)
  const directoryFromTree = slug && (opts?.categoryTree || []).length ? findDirectory(opts?.categoryTree || [], slug) : ''
  const directory = String(opts?.directory || directoryFromTree || slug)
  const ctx: PathContext = {
    cid: id > 0 ? String(id) : '',
    slug,
    category: slug,
    directory,
    year: '',
    month: '',
    day: ''
  }
  return buildByPattern(pattern, ctx)
}

export const resolvePostPath = (fields: Record<string, unknown> | undefined, path: string) => {
  return parseByPattern(readPostPattern(fields), path)
}

export const resolvePagePath = (fields: Record<string, unknown> | undefined, path: string) => {
  return parseByPattern(readPagePattern(fields), path)
}

export const resolveCategoryPath = (fields: Record<string, unknown> | undefined, path: string) => {
  return parseByPattern(readCategoryPattern(fields), path)
}

export const buildContentPathFromComment = (
  fields: Record<string, unknown> | undefined,
  item: { contentType?: string; contentSlug?: string; contentId?: number; commentPath?: string }
) => {
  const contentType = String(item.contentType || '').trim().toLowerCase()
  const commentPath = String(item.commentPath || '').trim()
  if (/^https?:\/\//i.test(commentPath)) {
    return commentPath
  }
  const hash = commentPath.includes('#') ? commentPath.slice(commentPath.indexOf('#')) : ''
  if (contentType === 'tweet') {
    return `/tweet${hash}`
  }
  if (contentType === 'page') {
    return `${buildPagePath(fields, { ID: item.contentId, slug: item.contentSlug })}${hash}`
  }
  return `${buildPostPath(fields, { ID: item.contentId, slug: item.contentSlug })}${hash}`
}

export const parseCategorySlugFromPath = (path: string) => {
  const parts = String(path || '')
    .split('/')
    .filter(Boolean)
  return parts.length ? parts[parts.length - 1] : ''
}

export const parsePageSlugFromPath = (path: string) => {
  const clean = trimTrailingSlash(String(path || '').trim())
  const parts = clean.split('/').filter(Boolean)
  const last = parts.length ? parts[parts.length - 1] : ''
  return last.endsWith('.html') ? last.slice(0, -5) : last
}

export const parsePostRouteParams = (route: { params: Record<string, unknown> }) => {
  const slug = String(route.params.slug || '')
  const cid = /^\d+$/.test(slug) ? Number(slug) : 0
  const year = String(route.params.year || '')
  const month = String(route.params.month || '')
  const day = String(route.params.day || '')
  const category = String(route.params.category || '')
  const directory = String(route.params.directory || '')
  return { slug, cid, year, month, day, category, directory }
}

export const readFieldRawValue = (fields: Record<string, unknown> | undefined, key: string) => readFieldValue(fields, key, '')
